import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useArticleBySlug, useArticles } from '../../hooks/useArticles';

const ArticleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { article, loading, error } = useArticleBySlug(slug);
  const { articles } = useArticles();

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a]"></div>
            <p className="mt-4 text-gray-600">Chargement de l'article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#1a1a1a] mb-4">Article non trouvé</h1>
            <p className="text-gray-600 mb-6">{error || 'Cet article n\'existe pas ou a été supprimé.'}</p>
            <Link to="/blog" className="text-[#1a1a1a] hover:underline cursor-pointer font-semibold">
              Retour au blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <span className="inline-block text-sm font-semibold px-4 py-2 rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">
              {article.category || 'Article'}
            </span>
          </div>

          <h1 className="text-5xl font-serif font-bold text-[#1a1a1a] mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-gray-500 mb-12 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>{new Date(article.published_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              <span>{article.readTime || '5 min'}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-user-line"></i>
              <span>{article.author || 'Équipe Giletau'}</span>
            </div>
          </div>

          {article.image_url && (
            <div className="w-full rounded-2xl overflow-hidden mb-12">
              <div className="w-full h-96">
                <img
                  src={article.image_url}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          )}

          <div className="prose prose-lg max-w-none">
            {article.meta_description && (
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                {article.meta_description}
              </p>
            )}

            <div 
              className="article-content text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1a1a1a] mb-12 text-center">
              Articles similaires
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/article/${relatedArticle.slug}`}
                  className="group cursor-pointer"
                >
                  <article className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all overflow-hidden h-full">
                    <div className="relative overflow-hidden">
                      <div className="w-full h-48">
                        <img
                          src={relatedArticle.image_url || 'https://public.readdy.ai/ai/img_res/b56b3dfd98a1b9ed80d6321a5614a55c.jpg'}
                          alt={relatedArticle.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-[#1a1a1a] mb-2 line-clamp-2">
                        {relatedArticle.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedArticle.meta_description || relatedArticle.content.substring(0, 100) + '...'}
                      </p>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ArticleDetail;
